import React, { useState, useEffect, useRef } from 'react';

const MathShooterPro = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [playerX, setPlayerX] = useState(50);
  const [bullets, setBullets] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [lives, setLives] = useState(5);
  const [ammo, setAmmo] = useState(20);
  const gameLoopRef = useRef(null);

  const generateEnemy = () => {
    const operations = [
      { op: '+', gen: () => {
        const a = Math.floor(Math.random() * 100) + 50;
        const b = Math.floor(Math.random() * 100) + 50;
        return { question: `${a}+${b}`, answer: a + b };
      }},
      { op: '-', gen: () => {
        const a = Math.floor(Math.random() * 200) + 100;
        const b = Math.floor(Math.random() * a);
        return { question: `${a}-${b}`, answer: a - b };
      }},
      { op: '×', gen: () => {
        const a = Math.floor(Math.random() * 20) + 10;
        const b = Math.floor(Math.random() * 15) + 2;
        return { question: `${a}×${b}`, answer: a * b };
      }},
      { op: '÷', gen: () => {
        const b = Math.floor(Math.random() * 12) + 2;
        const ans = Math.floor(Math.random() * 20) + 5;
        const a = b * ans;
        return { question: `${a}÷${b}`, answer: ans };
      }}
    ];

    const op = operations[Math.floor(Math.random() * operations.length)];
    const { question, answer } = op.gen();
    
    return {
      x: Math.random() * 90 + 5,
      y: 0,
      question,
      answer,
      speed: 1 + level * 0.2,
      id: Date.now() + Math.random()
    };
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setLives(5);
    setAmmo(20);
    setPlayerX(50);
    setBullets([]);
    setEnemies([generateEnemy()]);
  };

  useEffect(() => {
    if(gameState === 'playing') {
      gameLoopRef.current = setInterval(() => {
        // Move bullets
        setBullets(prev => prev.map(b => ({ ...b, y: b.y - 3 })).filter(b => b.y > 0));
        
        // Move enemies
        setEnemies(prev => {
          const updated = prev.map(e => ({ ...e, y: e.y + e.speed }));
          
          // Check if enemy reached bottom
          updated.forEach(e => {
            if(e.y > 95) {
              setLives(l => {
                const newLives = l - 1;
                if(newLives <= 0) setGameState('gameover');
                return newLives;
              });
            }
          });
          
          return updated.filter(e => e.y < 95);
        });

        // Check collisions
        bullets.forEach(bullet => {
          enemies.forEach(enemy => {
            if(Math.abs(bullet.x - enemy.x) < 5 && Math.abs(bullet.y - enemy.y) < 5) {
              if(bullet.answer === enemy.answer) {
                setScore(s => s + 50);
                setAmmo(a => a + 2);
                setEnemies(prev => prev.filter(e => e.id !== enemy.id));
                setBullets(prev => prev.filter(b => b.id !== bullet.id));
              }
            }
          });
        });

        // Spawn new enemies
        if(Math.random() < 0.02 + level * 0.005) {
          setEnemies(prev => [...prev, generateEnemy()]);
        }

        // Level up
        if(score > 0 && score % 500 === 0) {
          setLevel(l => l + 1);
        }
      }, 50);

      return () => clearInterval(gameLoopRef.current);
    }
  }, [gameState, bullets, enemies, score, level]);

  const shoot = (answer) => {
    if(ammo > 0 && gameState === 'playing') {
      setBullets(prev => [...prev, {
        x: playerX,
        y: 85,
        answer,
        id: Date.now() + Math.random()
      }]);
      setAmmo(a => a - 1);
    }
  };

  const handleKeyPress = (e) => {
    if(gameState !== 'playing') return;
    
    if(e.key === 'ArrowLeft') {
      setPlayerX(x => Math.max(5, x - 3));
    } else if(e.key === 'ArrowRight') {
      setPlayerX(x => Math.min(95, x + 3));
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-black p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-yellow-300">
          🎯 Math Shooter Pro 🔫
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🎯</div>
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Ready to Shoot?</h2>
            <p className="text-xl mb-8 text-gray-600">
              Shoot enemies with correct answers!<br/>
              Use ← → to move, click numbers to shoot
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START GAME
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold text-blue-600">Ammo: {ammo}</div>
              <div className="text-xl font-bold text-red-600">
                Lives: {'❤️'.repeat(lives)}
              </div>
            </div>

            <div className="relative bg-gradient-to-b from-blue-900 to-purple-900 rounded-lg overflow-hidden" style={{ height: '500px' }}>
              {/* Enemies */}
              {enemies.map(enemy => (
                <div
                  key={enemy.id}
                  className="absolute"
                  style={{
                    left: `${enemy.x}%`,
                    top: `${enemy.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="bg-red-500 rounded-lg p-3 shadow-lg">
                    <div className="text-white font-bold text-xl">{enemy.question}</div>
                    <div className="text-yellow-300 font-bold text-2xl text-center">{enemy.answer}</div>
                  </div>
                </div>
              ))}

              {/* Bullets */}
              {bullets.map(bullet => (
                <div
                  key={bullet.id}
                  className="absolute w-4 h-8 bg-yellow-400 rounded-full shadow-lg"
                  style={{
                    left: `${bullet.x}%`,
                    top: `${bullet.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <div className="text-xs font-bold text-center">{bullet.answer}</div>
                </div>
              ))}

              {/* Player */}
              <div
                className="absolute bottom-8 w-16 h-16 bg-blue-500 rounded-lg transform transition-all duration-100"
                style={{
                  left: `${playerX}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="text-3xl text-center">🚀</div>
              </div>
            </div>

            {/* Answer buttons */}
            <div className="grid grid-cols-5 gap-2 mt-4">
              {[...Array(10)].map((_, i) => {
                const num = i * 10 + Math.floor(Math.random() * 10);
                return (
                  <button
                    key={i}
                    onClick={() => shoot(num)}
                    className="bg-gradient-to-r from-green-500 to-blue-500 text-white py-3 rounded-lg font-bold text-xl hover:scale-105 transform transition"
                  >
                    {num}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">💥</div>
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Game Over!</h2>
            <div className="text-3xl mb-4 text-purple-600 font-bold">Final Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">Level Reached: {level}</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              PLAY AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MathShooterPro;
